import { useEffect, useMemo, useState } from "react";
import { Box, Button, IconButton, InputAdornment, Skeleton, TextField, Tooltip, Typography } from "@mui/material";
import { useForm } from "react-hook-form";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import NotificationsActiveOutlinedIcon from "@mui/icons-material/NotificationsActiveOutlined";
import PersonAddAltOutlinedIcon from "@mui/icons-material/PersonAddAltOutlined";
import StorefrontOutlinedIcon from "@mui/icons-material/StorefrontOutlined";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import SyncAltIcon from "@mui/icons-material/SyncAlt";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import LibraryAddCheckOutlinedIcon from "@mui/icons-material/LibraryAddCheckOutlined";
import PageTitle from "../PageTitle";
import { FormProvider } from "../../common/hook-form";
import { MerchantFilterCached } from "../../common/ProshipFormComponents";
import { apiConstants, getGeneric } from "../../common/constants";
import { useGetAllActiveMerchantsIdAndNameCachedQuery } from "../../redux/commonApiCalls";
import { EscalationAutoTagConfig, EscalationNotificationConfig } from "../../pages/WebHooks/MerchantConfig";
import EscalationV2Nav from "./EscalationV2Nav";
import EmsPageHeader from "./EmsPageHeader";
import { MUTED, NAVY } from "./SectionCard";
import { ACCENTS, CARD_SX, COLORS, rise } from "./emsTheme";
import { ConfigEmpty, Eyebrow, IconTile, OFF_GREY, Pill, StatePill, purpleControlsSx, topBarCardSx } from "./ConfigUi";

const wrapPillSx = { whiteSpace: "normal", textAlign: "left", borderRadius: "10px" };

/** One settings card: coloured top edge, icon tile, title, what it covers, and its saved state. */
const SettingsCard = ({ icon, accent, eyebrow, title, sub, state, order, children }) => (
    <Box sx={{ ...topBarCardSx(accent), ...rise(order) }}>
        <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5, px: 2.5, pt: 2.5, pb: 2, borderBottom: `1px solid ${COLORS.grid}`, flexWrap: "wrap" }}>
            <IconTile icon={icon} accent={accent} size={40} />
            <Box sx={{ flex: "1 1 200px", minWidth: 0 }}>
                <Eyebrow color={accent.solid}>{eyebrow}</Eyebrow>
                <Typography component="h2" sx={{ fontSize: 18, fontWeight: 800, color: NAVY, lineHeight: 1.3 }}>{title}</Typography>
                <Typography sx={{ fontSize: 13, color: MUTED }}>{sub}</Typography>
            </Box>
            {state}
        </Box>
        <Box sx={{ p: { xs: 2, sm: 2.5 } }}>{children}</Box>
    </Box>
);

/** The merchants picked so far, beside the settings: who a save will apply to, each removable. */
const SelectedMerchantsPanel = ({ ids, nameOf, onRemove, onClear }) => {
    const [query, setQuery] = useState("");
    const q = query.trim().toLowerCase();
    const shown = q ? ids.filter((id) => nameOf(id).toLowerCase().includes(q)) : ids;
    return (
        <Box sx={{ ...topBarCardSx(ACCENTS.purple), position: { lg: "sticky" }, top: { lg: 16 }, ...rise(2) }}>
            <Box sx={{ px: 2, pt: 2.25, pb: 1.5, borderBottom: `1px solid ${COLORS.grid}`, display: "flex", alignItems: "center", gap: 1 }}>
                <IconTile icon={LibraryAddCheckOutlinedIcon} size={34} />
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Eyebrow>Applies to</Eyebrow>
                    <Typography sx={{ fontSize: 15, fontWeight: 800, color: NAVY, lineHeight: 1.3 }}>
                        {ids.length} {ids.length === 1 ? "merchant" : "merchants"}
                    </Typography>
                </Box>
                {ids.length > 1 && (
                    <Button size="small" onClick={onClear} sx={{ textTransform: "none", fontWeight: 700, color: COLORS.ink3 }}>Clear all</Button>
                )}
            </Box>
            {ids.length > 8 && (
                <Box sx={{ px: 1.5, pt: 1.5 }}>
                    <TextField size="small" fullWidth placeholder="Find a selected merchant" value={query} onChange={(e) => setQuery(e.target.value)}
                        InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 18 }} /></InputAdornment> }}
                        sx={{ "& .MuiInputBase-input": { fontSize: 13 } }} />
                </Box>
            )}
            <Box component="ul" sx={{ listStyle: "none", m: 0, p: 1, maxHeight: { xs: 220, lg: "calc(100vh - 260px)" }, overflowY: "auto" }}>
                {shown.map((id) => (
                    <Box component="li" key={id} sx={{ display: "flex", alignItems: "center", gap: 1, px: 1, py: 0.5, borderRadius: "8px",
                        "&:hover": { bgcolor: COLORS.ground }, "&:hover .remove": { opacity: 1 } }}>
                        <Box aria-hidden sx={{ width: 26, height: 26, borderRadius: "8px", bgcolor: COLORS.purpleSoft, color: COLORS.purple, flexShrink: 0,
                            display: "grid", placeItems: "center", fontSize: 12, fontWeight: 800 }}>
                            {(nameOf(id).trim()[0] || "?").toUpperCase()}
                        </Box>
                        <Typography title={nameOf(id)} sx={{ flex: 1, minWidth: 0, fontSize: 13, color: COLORS.ink, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                            {nameOf(id)}
                        </Typography>
                        <Tooltip title="Remove">
                            <IconButton className="remove" size="small" aria-label={`Remove ${nameOf(id)}`} onClick={() => onRemove(id)}
                                sx={{ opacity: { xs: 1, lg: 0.45 }, transition: "opacity 120ms" }}>
                                <CloseIcon sx={{ fontSize: 16 }} />
                            </IconButton>
                        </Tooltip>
                    </Box>
                ))}
                {shown.length === 0 && (
                    <Typography component="li" sx={{ fontSize: 13, color: MUTED, p: 1 }}>No selected merchant matches &quot;{query}&quot;.</Typography>
                )}
            </Box>
        </Box>
    );
};

/**
 * Auto Tagging & Email Config tab — per merchant: who new tickets are auto-assigned to, and which
 * ticket updates email the merchant's contacts. Same forms and same saved settings as Merchant
 * Config ("Escalation Desk — Auto-Tagging" / "— Notifications"); editing either place changes both.
 * The route and the backend both need ESCALATION_ADMIN, so whoever gets here can edit.
 *
 * Several merchants can be picked (Select all included): the forms then start blank and one save
 * applies the setting to every picked merchant, listed in the panel on the left.
 */
const EmailSettingsPage = () => {
    const methods = useForm({ defaultValues: { merchant: "" } });
    const picked = methods.watch("merchant");
    // The picker stores its picks as one comma-joined string.
    const merchantIds = useMemo(() => String(picked || "").split(",").filter(Boolean), [picked]);
    const merchant = merchantIds.length === 1 ? merchantIds[0] : "";
    const bulk = merchantIds.length > 1;
    const { data: merchantList = [] } = useGetAllActiveMerchantsIdAndNameCachedQuery();
    const nameById = useMemo(() => {
        const m = {};
        merchantList.forEach((x) => { m[x.id] = x.companyName ? `${x.name} (${x.companyName})` : x.name; });
        return m;
    }, [merchantList]);
    const nameOf = (id) => nameById[id] || id;
    const setPicked = (ids) => methods.setValue("merchant", ids.join(","));
    // Settings are kept with the merchant they were loaded for. Until the picked merchant's own
    // settings arrive, nothing is shown, so a save can never write one merchant's settings onto another.
    const [loaded, setLoaded] = useState({ merchant: null, config: null, failed: false });
    const [reload, setReload] = useState(0);

    useEffect(() => {
        if (!merchant) return undefined;
        let cancelled = false;
        getGeneric(`${apiConstants.ESCALATION_MERCHANT_SETTINGS_V2}/${merchant}`)
            .then((res) => { if (!cancelled) setLoaded({ merchant, config: res?.data || {}, failed: false }); })
            .catch(() => { if (!cancelled) setLoaded({ merchant, config: null, failed: true }); });
        return () => { cancelled = true; };
    }, [merchant, reload]);

    const current = merchant && loaded.merchant === merchant ? loaded : null;
    const merchantConfig = current?.config || null;

    // What is saved right now (not what's being edited), for the state pill on each card.
    const autoTag = merchantConfig?.escalationAutoTagConfig;
    const autoTagOn = autoTag?.enabled === true || autoTag?.enabled === "true";
    const assignees = (autoTag?.assignedUserIds || []).length;
    const notify = merchantConfig?.escalationNotificationConfig;
    const notifyOn = notify?.enabled === true || notify?.enabled === "true";

    return (
        <>
            <PageTitle>Escalation Management V2 — Auto Tagging & Email Config</PageTitle>
            <EmsPageHeader icon={MailOutlineIcon} title="Auto Tagging & Email Config"
                sub="Per merchant: who new tickets are auto-assigned to, and which ticket updates email the merchant's contacts." />
            <EscalationV2Nav />

            {/* Merchant picker */}
            <Box sx={{ ...topBarCardSx(ACCENTS.purple), p: { xs: 2, sm: 2.5 }, pt: { xs: 2.5, sm: 3 }, mb: 2, display: "flex", gap: 2, alignItems: "center",
                flexWrap: "wrap", ...rise(1) }}>
                <Box sx={{ display: "flex", gap: 1.5, alignItems: "center", flex: "1 1 260px", minWidth: 0 }}>
                    <IconTile icon={StorefrontOutlinedIcon} size={40} />
                    <Box sx={{ minWidth: 0 }}>
                        <Eyebrow>Merchant</Eyebrow>
                        <Typography sx={{ fontSize: 16, fontWeight: 800, color: NAVY, lineHeight: 1.3 }}>Whose settings do you want to see or change?</Typography>
                        <Typography sx={{ fontSize: 12, color: MUTED }}>
                            <SyncAltIcon sx={{ fontSize: 14, verticalAlign: "-2px", mr: 0.5 }} aria-hidden />
                            Same settings as Merchant Config → &quot;Escalation Desk&quot;, so a change here shows there too. Pick several (or Select all) to change them together.
                        </Typography>
                    </Box>
                </Box>
                <Box sx={{ flex: { xs: "1 1 100%", sm: "0 1 380px" }, minWidth: 0, ...purpleControlsSx }}>
                    <FormProvider methods={methods}>
                        <MerchantFilterCached multiple selectAll />
                    </FormProvider>
                </Box>
            </Box>

            {merchantIds.length === 0 ? (
                <ConfigEmpty icon={StorefrontOutlinedIcon} title="Pick a merchant to begin"
                    text="Select a merchant to see its auto tagging and email settings, or several to set them all at once.">
                    <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", justifyContent: "center", mt: 0.5 }}>
                        <Pill icon={PersonAddAltOutlinedIcon} sx={wrapPillSx}>Auto tagging: who new tickets go to</Pill>
                        <Pill accent={ACCENTS.blue} icon={NotificationsActiveOutlinedIcon} sx={wrapPillSx}>Ticket emails: when requesters hear back</Pill>
                    </Box>
                </ConfigEmpty>
            ) : (
            <Box sx={{ display: "grid", gridTemplateColumns: { xs: "minmax(0, 1fr)", lg: "300px minmax(0, 1fr)" }, gap: 2, alignItems: "start" }}>
                <SelectedMerchantsPanel ids={merchantIds} nameOf={nameOf}
                    onRemove={(id) => setPicked(merchantIds.filter((x) => x !== id))} onClear={() => setPicked([])} />
            {bulk ? (
                <Box sx={{ display: "grid", gridTemplateColumns: { xs: "minmax(0, 1fr)", xl: "minmax(0, 1fr) minmax(0, 1fr)" }, gap: 2, alignItems: "start" }}>
                    <SettingsCard icon={PersonAddAltOutlinedIcon} accent={ACCENTS.purple} eyebrow="Assignment" title="Auto tagging" order={2}
                        sub={`Who new tickets for the ${merchantIds.length} selected merchants are assigned to automatically`}
                        state={<Pill accent={OFF_GREY}>{merchantIds.length} merchants</Pill>}>
                        <EscalationAutoTagConfig key="bulk" merchantIds={merchantIds} merchantConfig={null} setReload={setReload} stacked />
                    </SettingsCard>
                    <SettingsCard icon={NotificationsActiveOutlinedIcon} accent={ACCENTS.blue} eyebrow="Notifications" title="Ticket emails" order={3}
                        sub={`Applies to every ticket for the ${merchantIds.length} selected merchants`}
                        state={<Pill accent={OFF_GREY}>{merchantIds.length} merchants</Pill>}>
                        <EscalationNotificationConfig key="bulk" merchantIds={merchantIds} merchantConfig={null} setReload={setReload} stacked />
                    </SettingsCard>
                </Box>
            ) : current?.failed ? (
                <ConfigEmpty icon={ErrorOutlineIcon} title="Couldn't load this merchant's settings" text="Nothing has been changed.">
                    <Button variant="outlined" size="small" onClick={() => setReload((r) => r + 1)} sx={{ mt: 0.5, textTransform: "none" }}>Try again</Button>
                </ConfigEmpty>
            ) : !merchantConfig ? (
                <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", xl: "1fr 1fr" }, gap: 2 }}>
                    {[0, 1].map((i) => (
                        <Box key={i} sx={{ ...CARD_SX, p: 2.5 }} aria-busy="true">
                            <Box sx={{ display: "flex", gap: 1.5, alignItems: "center", mb: 2 }}>
                                <Skeleton variant="rounded" width={40} height={40} />
                                <Box sx={{ flex: 1 }}><Skeleton width="40%" /><Skeleton width="70%" /></Box>
                            </Box>
                            <Skeleton variant="rounded" height={72} sx={{ mb: 1.5 }} />
                            <Skeleton variant="rounded" height={96} />
                        </Box>
                    ))}
                </Box>
            ) : (
                <Box sx={{ display: "grid", gridTemplateColumns: { xs: "minmax(0, 1fr)", xl: "minmax(0, 1fr) minmax(0, 1fr)" }, gap: 2, alignItems: "start" }}>
                    <SettingsCard icon={PersonAddAltOutlinedIcon} accent={ACCENTS.purple} eyebrow="Assignment" title="Auto tagging" order={2}
                        sub="Who new tickets for this merchant are assigned to automatically"
                        state={autoTag ? <StatePill on={autoTagOn} onLabel={`Saved: on · ${assignees} ${assignees === 1 ? "person" : "people"}`} offLabel="Saved: off" />
                            : <Pill accent={OFF_GREY}>Not set yet</Pill>}>
                        <EscalationAutoTagConfig key={merchant} merchant={merchant} merchantConfig={merchantConfig} setReload={setReload} stacked />
                    </SettingsCard>
                    <SettingsCard icon={NotificationsActiveOutlinedIcon} accent={ACCENTS.blue} eyebrow="Notifications" title="Ticket emails" order={3}
                        sub="Applies to every ticket for this merchant"
                        state={notify ? <StatePill on={notifyOn} onLabel="Saved: on" offLabel="Saved: off" /> : <Pill accent={OFF_GREY}>Not set yet</Pill>}>
                        <EscalationNotificationConfig key={merchant} merchant={merchant} merchantConfig={merchantConfig} setReload={setReload} stacked />
                    </SettingsCard>
                </Box>
            )}
            </Box>
            )}
        </>
    );
};

export default EmailSettingsPage;
